import { useCallback } from 'react';
import { appQueryParamsSelector } from '@doda/common/src/redux/features/queryParams';
import { useSelector } from 'react-redux';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import { moveToSearchResultPage } from '../logics/moveToSearchResultPage';

/**
 * カスタムフックを使用して、検索処理を管理する
 *
 * @param {SelectedCurrentConditionType} selectedSearchCondition - 現在選択されている検索条件
 * @param {number} oldestDayWdtno - 最古の日付のWDT番号
 * @returns {{onClickSearchButton: Function, onClickOccupationModalSearchButton: Function, onClickLocationModalSearchButton: Function, onClickRouteStationModalSearchButton: Function, onClickIndustryModalSearchButton: Function}} 検索処理を実行する関数
 */
export const useSearch = (
  selectedSearchCondition: SelectedCurrentConditionType,
  oldestDayWdtno: number
) => {
  const { qc } = useSelector(appQueryParamsSelector);

  // サイドバー上での検索処理
  const onClickSearchButton = useCallback(() => {
    moveToSearchResultPage(selectedSearchCondition, oldestDayWdtno, qc);
  }, [oldestDayWdtno, qc, selectedSearchCondition]);

  // 職種モーダルでの検索処理
  const onClickOccupationModalSearchButton = useCallback(
    (selectedOccupation: {
      searchJobTypeLId: SelectedCurrentConditionType['searchJobTypeLId'];
      searchJobTypeMId: SelectedCurrentConditionType['searchJobTypeMId'];
      searchJobTypeSId: SelectedCurrentConditionType['searchJobTypeSId'];
    }) => {
      moveToSearchResultPage(
        {
          ...selectedSearchCondition,
          ...selectedOccupation,
        },
        oldestDayWdtno,
        qc
      );
    },
    [oldestDayWdtno, qc, selectedSearchCondition]
  );

  // 勤務地モーダルでの検索処理
  const onClickLocationModalSearchButton = useCallback(
    (selectedLocation: {
      areaId: string[];
      prefectureId: string[];
      cityId: string[];
      wardId: string[];
    }) => {
      moveToSearchResultPage(
        { ...selectedSearchCondition, ...selectedLocation },
        oldestDayWdtno,
        qc
      );
    },
    [oldestDayWdtno, qc, selectedSearchCondition]
  );

  // 路線駅モーダルでの検索処理
  const onClickRouteStationModalSearchButton = useCallback(
    (selectedRouteStation: { routeId: string[]; stationId: string[] }) => {
      moveToSearchResultPage(
        { ...selectedSearchCondition, ...selectedRouteStation },
        oldestDayWdtno,
        qc
      );
    },
    [oldestDayWdtno, qc, selectedSearchCondition]
  );

  // 業種モーダルでの検索処理
  const onClickIndustryModalSearchButton = useCallback(
    (selectedIndustry: { industryLId: string[]; industrySId: string[] }) => {
      moveToSearchResultPage(
        {
          ...selectedSearchCondition,
          ...selectedIndustry,
        },
        oldestDayWdtno,
        qc
      );
    },
    [oldestDayWdtno, qc, selectedSearchCondition]
  );

  return {
    onClickSearchButton,
    onClickOccupationModalSearchButton,
    onClickLocationModalSearchButton,
    onClickRouteStationModalSearchButton,
    onClickIndustryModalSearchButton,
  };
};
